/* ---------------------------------------------------------------------------
   Pakistan's national outline, bundled — and a boundary decision written down.

   READ THE SECOND HALF OF THIS HEADER BEFORE CHANGING THE OUTLINE OR QUOTING IT.

   The register's map draws OpenStreetMap tiles, which need a network. This is
   what it draws when they do not arrive: an outline of the country, from the
   app's own bundle, with the structures plotted on it — see src/map/nepalMap.ts
   for how the two layers relate, and src/data/nepal.ts for the same thing done
   for the deployment this application was written for.

   PROVENANCE. Natural Earth 1:10m Admin 0 Countries, **Pakistan point-of-view
   edition** (`ne_10m_admin_0_countries_pak`), which is public domain —
   https://www.naturalearthdata.com/about/terms-of-use/. The largest ring of
   Pakistan's polygon, simplified by Douglas–Peucker at a 0.0126° tolerance
   (about 1.4 km) from 2278 points to the 652 below, and rounded to four decimal
   places (about 11 m).

   Regenerated rather than hand-edited, and the script is in the repository this
   time — `npm run gen:outline -- --country PAK --pov pak` prints exactly the
   array below. The same script reproduces Nepal's committed 267 points byte for
   byte from the default edition, which is how it was checked before being
   trusted with a second country.

   MORE POINTS THAN NEPAL'S, and deliberately. The tolerance is the constant held
   across countries, not the point count: Pakistan's boundary is longer and more
   intricate, so at the same cartographic fidelity it is honestly 652 points
   rather than the same 267 drawn coarser.

   ── THE POINT-OF-VIEW EDITION, WHICH IS THE PART THAT NEEDED DECIDING ──

   Natural Earth publishes a default admin-0 boundary set and about thirty
   POINT-OF-VIEW editions beside it, each drawing disputed boundaries as one
   government draws them. For Pakistan the two are not a rounding apart. Measured
   between the source rings, the `_pak` edition departs from the default edition
   by **up to about 380 km** — near Muzaffarabad on one side and in Ladakh on the
   other. The default edition stops at 77.05°E and carves out a separate
   "Siachen Glacier / Kashmir / Indeterminate" entity; the `_pak` edition runs to
   79.62°E and does not.

   THE RULE THIS FILE FOLLOWS: a tenant's map draws its own government's point of
   view, where Natural Earth publishes one, and the data file says so in as many
   words. The alternative is not neutrality — it is some other government's point
   of view printed over this one's bridge register.

   WHAT THAT IS AND IS NOT. It is a choice about which published cartography to
   bundle for one department's deployment, and it is scoped to that deployment:
   `pk-fwo` draws this, `nepal-dor` draws Nepal's, and neither is visible to the
   other. It adjudicates nothing. It is not evidence of anything. It is NOT a
   survey boundary, it must not be used to decide what is inside Pakistan or
   which side of any line anything falls on, and nothing in this application
   reads it for any purpose but drawing a backdrop when no tile arrives.

   NEPAL IS ON THE DEFAULT EDITION, WHICH THIS RULE SAYS IT SHOULD NOT BE. The
   267 points in src/data/nepal.ts came from the default file, and the `_nep`
   edition differs from it by up to about 6.6 km in the far north-west — the
   Kalapani sector. That is a live inconsistency between two tenants and it is
   recorded rather than quietly fixed: re-cutting a border already shipped on a
   government's own register is a decision for the people who own that
   relationship, not a tidy-up. `GR-34` in ROADMAP.md, and it is one command.

   ── WHAT IS DROPPED ──

   The largest ring only, as Nepal's was. The generator reported two others and
   both are left out: a 16-point island group off Karachi (67.38–67.49°E), which
   is a pixel at register scale, and a 97-point ring at 69.84–71.70°E,
   20.71–21.81°E — Junagadh, which the `_pak` edition carries and which is
   several hundred kilometres from the nearest point of the mainland ring. A
   detached ring cannot be drawn by a single closed polyline anyway; that it also
   avoids restating a claim the map has no business restating is a second reason
   and not the first.
   --------------------------------------------------------------------------- */

import type { LngLat } from './nepal.ts';
import type { GeoExtent } from '../tenant/types.ts';

/**
 * The outline, as a closed ring — the last point repeats the first, so a
 * consumer can draw it as a polyline without joining the ends itself.
 */
export const PAKISTAN_OUTLINE: readonly LngLat[] = [
  [77.8003, 35.4954],
  [77.8342, 35.4522],
  [77.8831, 35.4311],
  [77.9859, 35.4942],
  [78.0443, 35.4916],
  [78.0556, 35.4529],
  [78.0098, 35.3475],
  [78.0012, 35.2689],
  [78.13, 35.0554],
  [78.1481, 34.9431],
  [78.2114, 34.8483],
  [78.296, 34.6247],
  [78.3794, 34.5787],
  [78.6646, 34.5264],
  [78.8017, 34.415],
  [78.9512, 34.3492],
  [78.9854, 34.2864],
  [78.9762, 34.2172],
  [78.9032, 34.1581],
  [78.7301, 34.0793],
  [78.7211, 33.9944],
  [78.7873, 33.8084],
  [78.7814, 33.5528],
  [78.8009, 33.4942],
  [78.965, 33.3443],
  [79.075, 33.2957],
  [79.4201, 33.2752],
  [79.4561, 33.2504],
  [79.4235, 33.1578],
  [79.3732, 33.1121],
  [79.3993, 33.0269],
  [79.3398, 33.0133],
  [79.3336, 32.9943],
  [79.4141, 32.9721],
  [79.5641, 32.7939],
  [79.6139, 32.765],
  [79.6207, 32.7287],
  [79.4435, 32.5348],
  [79.4068, 32.5297],
  [79.3795, 32.5765],
  [79.3205, 32.5923],
  [79.2874, 32.5326],
  [79.1129, 32.4724],
  [79.0753, 32.3708],
  [79.0282, 32.3771],
  [78.9434, 32.3464],
  [78.7715, 32.4617],
  [78.7369, 32.5458],
  [78.751, 32.6128],
  [78.7133, 32.6375],
  [78.6302, 32.578],
  [78.4082, 32.5584],
  [78.3134, 32.4717],
  [78.2822, 32.4807],
  [78.2704, 32.5042],
  [78.2994, 32.5887],
  [78.3778, 32.6573],
  [78.3834, 32.7144],
  [78.3604, 32.7676],
  [78.3177, 32.7647],
  [78.3143, 32.7354],
  [78.2437, 32.6929],
  [78.1146, 32.6677],
  [78.059, 32.6183],
  [78.0089, 32.6103],
  [77.9385, 32.6953],
  [77.9289, 32.7624],
  [77.8579, 32.873],
  [77.7123, 33.0145],
  [77.5535, 32.9234],
  [77.3487, 32.8672],
  [77.0885, 32.9502],
  [76.9401, 33.0343],
  [76.7545, 33.1946],
  [76.6259, 33.1893],
  [76.5424, 33.2329],
  [76.3967, 33.1977],
  [76.2943, 33.1479],
  [76.237, 33.0635],
  [76.1008, 33.013],
  [75.9547, 32.9014],
  [75.8696, 32.94],
  [75.8264, 32.9381],
  [75.8049, 32.8986],
  [75.9093, 32.7727],
  [75.9014, 32.7029],
  [75.926, 32.6412],
  [75.8391, 32.5066],
  [75.743, 32.4644],
  [75.6983, 32.406],
  [75.5733, 32.3746],
  [75.5024, 32.3006],
  [75.4447, 32.3303],
  [75.4419, 32.3598],
  [75.3904, 32.3355],
  [75.2984, 32.3324],
  [75.359, 32.2617],
  [75.2947, 32.1489],
  [75.1958, 32.1286],
  [75.1741, 32.0868],
  [75.1021, 32.0781],
  [75.0408, 32.098],
  [75.0226, 32.0947],
  [75.0302, 32.0664],
  [74.9862, 32.0632],
  [74.9557, 32.0322],
  [74.8891, 32.0289],
  [74.8732, 32.0117],
  [74.8286, 32.0254],
  [74.7837, 31.9429],
  [74.6984, 31.9502],
  [74.67, 31.9042],
  [74.5504, 31.827],
  [74.4894, 31.7112],
  [74.5556, 31.6122],
  [74.6172, 31.4589],
  [74.5322, 31.3032],
  [74.5064, 31.1754],
  [74.5368, 31.1152],
  [74.617, 31.1153],
  [74.6588, 31.0838],
  [74.5795, 31.0427],
  [74.5193, 30.9625],
  [74.4416, 30.9462],
  [74.4013, 30.8931],
  [74.3192, 30.8934],
  [74.3002, 30.8382],
  [74.2567, 30.8153],
  [74.2667, 30.7884],
  [74.1637, 30.6649],
  [74.0674, 30.6105],
  [74.0575, 30.5314],
  [74.0044, 30.5089],
  [73.9293, 30.4191],
  [73.8999, 30.4221],
  [73.9061, 30.3948],
  [73.8517, 30.3727],
  [73.8423, 30.353],
  [73.8916, 30.3298],
  [73.9346, 30.261],
  [73.9444, 30.1883],
  [73.7397, 30.0485],
  [73.5578, 30.0125],
  [73.3703, 29.9273],
  [73.2329, 29.5366],
  [72.9304, 29.0477],
  [72.3546, 28.7672],
  [72.2565, 28.6456],
  [72.1978, 28.4449],
  [72.1499, 28.3538],
  [71.9882, 28.2281],
  [71.9083, 28.1356],
  [71.8744, 27.9597],
  [71.5607, 27.8685],
  [71.2263, 27.8454],
  [70.9138, 27.7178],
  [70.8316, 27.7015],
  [70.7618, 27.7098],
  [70.7104, 27.7412],
  [70.6407, 27.8548],
  [70.6332, 27.9316],
  [70.5349, 28.016],
  [70.4562, 28.0398],
  [70.3239, 28.0004],
  [70.102, 27.8117],
  [69.9935, 27.5711],
  [69.9081, 27.4973],
  [69.848, 27.4104],
  [69.5756, 27.1884],
  [69.5344, 27.1256],
  [69.4651, 26.8078],
  [69.4728, 26.7666],
  [69.5042, 26.7352],
  [69.6594, 26.6777],
  [69.7723, 26.5951],
  [69.8156, 26.5803],
  [70.0561, 26.5891],
  [70.1296, 26.5625],
  [70.1581, 26.5301],
  [70.1469, 26.2174],
  [70.0739, 26.0831],
  [70.0646, 25.9803],
  [70.0832, 25.9299],
  [70.214, 25.7863],
  [70.2646, 25.6973],
  [70.3601, 25.6735],
  [70.4772, 25.6763],
  [70.5923, 25.7088],
  [70.6323, 25.7014],
  [70.6539, 25.6745],
  [70.6545, 25.3966],
  [70.7105, 25.3359],
  [70.7349, 25.2673],
  [70.8486, 25.1633],
  [70.9152, 24.9466],
  [71.0639, 24.6826],
  [70.9771, 24.6397],
  [70.9548, 24.5845],
  [70.9809, 24.5347],
  [70.9771, 24.4649],
  [71.075, 24.4364],
  [71.0731, 24.4021],
  [70.9967, 24.3566],
  [70.9177, 24.3617],
  [70.8409, 24.3058],
  [70.8515, 24.2649],
  [70.7555, 24.2314],
  [70.5847, 24.2579],
  [70.5602, 24.2873],
  [70.5456, 24.3622],
  [70.5752, 24.4],
  [70.5629, 24.4241],
  [70.5209, 24.4249],
  [70.1097, 24.3049],
  [70.0521, 24.2021],
  [69.972, 24.1652],
  [69.7146, 24.1686],
  [69.5631, 24.2768],
  [69.2808, 24.2837],
  [69.1667, 24.2532],
  [69.0677, 24.2884],
  [68.9626, 24.2572],
  [68.9042, 24.3206],
  [68.8388, 24.2365],
  [68.7992, 24.3291],
  [68.7473, 24.3312],
  [68.7259, 24.2892],
  [68.7245, 23.9652],
  [68.4316, 23.9671],
  [68.3536, 23.9393],
  [68.3489, 23.9646],
  [68.3304, 23.9666],
  [68.3141, 23.9159],
  [68.2639, 23.9359],
  [68.2604, 23.8955],
  [68.2073, 23.877],
  [68.183, 23.8421],
  [68.1575, 23.9051],
  [68.1706, 23.8399],
  [68.1433, 23.7611],
  [68.1491, 23.6945],
  [68.1022, 23.7338],
  [68.0989, 23.7093],
  [68.0628, 23.7231],
  [68.0539, 23.7437],
  [68.1022, 23.8157],
  [68.0594, 23.8619],
  [68.0637, 23.9232],
  [68.0135, 23.9393],
  [68.0539, 23.8294],
  [68.0135, 23.7685],
  [67.9942, 23.8112],
  [67.9509, 23.8089],
  [67.9099, 23.8362],
  [67.8999, 23.8676],
  [67.8689, 23.8505],
  [67.8552, 23.9119],
  [67.8317, 23.8095],
  [67.7186, 23.8437],
  [67.7437, 23.8088],
  [67.7157, 23.7917],
  [67.6316, 23.8035],
  [67.6504, 23.9331],
  [67.6145, 23.8501],
  [67.5884, 23.8847],
  [67.5746, 23.8718],
  [67.5466, 23.8983],
  [67.5301, 23.8913],
  [67.5466, 23.871],
  [67.4785, 23.8915],
  [67.5125, 23.8983],
  [67.5405, 23.9393],
  [67.4989, 23.9256],
  [67.5064, 23.974],
  [67.4512, 23.9877],
  [67.5331, 24.0703],
  [67.5098, 24.0521],
  [67.354, 24.0709],
  [67.3413, 24.0864],
  [67.2791, 24.2812],
  [67.286, 24.3296],
  [67.3625, 24.3439],
  [67.2717, 24.3691],
  [67.2866, 24.4395],
  [67.2588, 24.4191],
  [67.2109, 24.5084],
  [67.2308, 24.5251],
  [67.1946, 24.5473],
  [67.1836, 24.5903],
  [67.2526, 24.5903],
  [67.1521, 24.6157],
  [67.1755, 24.6887],
  [67.2555, 24.748],
  [67.2327, 24.7707],
  [67.1673, 24.7602],
  [67.1397, 24.8033],
  [67.0812, 24.8033],
  [67.0825, 24.7742],
  [66.9651, 24.8436],
  [66.9651, 24.81],
  [66.8821, 24.851],
  [66.6541, 24.8392],
  [66.7044, 24.892],
  [66.6813, 24.9105],
  [66.6813, 24.9489],
  [66.6996, 25.0853],
  [66.7372, 25.1811],
  [66.7102, 25.2392],
  [66.5479, 25.3656],
  [66.5912, 25.3996],
  [66.531, 25.5181],
  [66.4928, 25.5181],
  [66.437, 25.5977],
  [66.3877, 25.5924],
  [66.3755, 25.6257],
  [66.2039, 25.5932],
  [66.0944, 25.4748],
  [66.2131, 25.4914],
  [66.2581, 25.5431],
  [66.3364, 25.5534],
  [66.4232, 25.481],
  [66.4562, 25.496],
  [66.4922, 25.481],
  [66.4575, 25.4538],
  [66.5116, 25.4142],
  [66.4984, 25.3985],
  [66.3595, 25.4652],
  [66.2518, 25.4729],
  [66.1592, 25.4469],
  [65.8584, 25.4175],
  [65.6521, 25.3445],
  [65.4678, 25.3854],
  [65.3823, 25.3712],
  [65.3954, 25.3923],
  [65.2923, 25.365],
  [65.2323, 25.3707],
  [65.1553, 25.2961],
  [64.9207, 25.3271],
  [64.7444, 25.324],
  [64.696, 25.2976],
  [64.6686, 25.2551],
  [64.6686, 25.2067],
  [64.7102, 25.1794],
  [64.655, 25.1657],
  [64.6004, 25.1732],
  [64.6277, 25.2285],
  [64.5654, 25.2703],
  [64.5184, 25.2756],
  [64.492, 25.2457],
  [64.4393, 25.2346],
  [64.3607, 25.283],
  [64.0945, 25.3359],
  [64.1069, 25.3854],
  [64.0796, 25.4162],
  [64.1509, 25.4321],
  [64.1507, 25.4514],
  [64.1273, 25.4538],
  [64.0454, 25.4503],
  [63.9766, 25.4128],
  [63.9971, 25.4196],
  [64.0117, 25.3973],
  [64.0488, 25.4165],
  [64.0796, 25.365],
  [64.0597, 25.3446],
  [63.9441, 25.3349],
  [63.7378, 25.3853],
  [63.5725, 25.365],
  [63.4958, 25.2914],
  [63.4695, 25.2893],
  [63.5187, 25.2209],
  [63.5005, 25.1937],
  [63.1441, 25.2609],
  [63.0278, 25.2204],
  [62.7705, 25.2619],
  [62.4772, 25.2591],
  [62.4676, 25.2235],
  [62.4894, 25.2142],
  [62.4138, 25.2065],
  [62.3431, 25.1555],
  [62.3391, 25.1247],
  [62.3872, 25.1016],
  [62.3811, 25.0876],
  [62.2561, 25.0986],
  [62.3153, 25.1302],
  [62.2975, 25.1851],
  [62.2542, 25.212],
  [62.1452, 25.2184],
  [62.08, 25.1972],
  [62.059, 25.1616],
  [62.0972, 25.1071],
  [61.8811, 25.103],
  [61.8473, 25.0379],
  [61.7549, 25.0135],
  [61.7201, 25.0374],
  [61.7831, 25.188],
  [61.7495, 25.1732],
  [61.6942, 25.2005],
  [61.5882, 25.2021],
  [61.62, 25.2855],
  [61.6432, 25.6133],
  [61.6735, 25.6583],
  [61.6604, 25.7692],
  [61.6814, 25.7955],
  [61.7545, 25.82],
  [61.8329, 26.2254],
  [62.0942, 26.3148],
  [62.1238, 26.374],
  [62.2651, 26.361],
  [62.249, 26.4165],
  [62.2979, 26.474],
  [62.292, 26.5012],
  [62.4185, 26.5623],
  [62.7019, 26.604],
  [62.7536, 26.6442],
  [63.1413, 26.6254],
  [63.1743, 26.685],
  [63.1896, 26.846],
  [63.2616, 26.8717],
  [63.235, 26.9411],
  [63.2314, 27.0658],
  [63.2609, 27.1156],
  [63.3196, 27.1173],
  [63.2713, 27.2034],
  [63.1771, 27.2584],
  [62.8924, 27.2135],
  [62.8006, 27.2252],
  [62.7424, 27.2668],
  [62.7788, 27.2998],
  [62.756, 27.3481],
  [62.8147, 27.495],
  [62.7803, 27.8297],
  [62.7357, 27.995],
  [62.7615, 28.2464],
  [62.7393, 28.2583],
  [62.5756, 28.229],
  [62.4923, 28.3007],
  [62.4643, 28.3498],
  [62.3628, 28.4189],
  [62.0491, 28.4863],
  [61.8928, 28.5426],
  [61.653, 28.7563],
  [61.5665, 28.871],
  [61.5109, 29.0074],
  [61.468, 29.0417],
  [61.4877, 29.0807],
  [61.3979, 29.1591],
  [61.387, 29.2066],
  [61.3328, 29.2749],
  [61.3374, 29.3744],
  [61.2792, 29.4066],
  [60.8444, 29.8582],
  [62.4775, 29.4078],
  [63.5686, 29.4975],
  [63.972, 29.4296],
  [64.0861, 29.3866],
  [64.1134, 29.3963],
  [64.1726, 29.4843],
  [64.4777, 29.5704],
  [64.8203, 29.5679],
  [65.0364, 29.5402],
  [66.1956, 29.8353],
  [66.2752, 29.8852],
  [66.3405, 29.9566],
  [66.2193, 30.0579],
  [66.3053, 30.2448],
  [66.3218, 30.4374],
  [66.2649, 30.5578],
  [66.268, 30.6014],
  [66.3663, 30.9229],
  [66.3923, 30.9446],
  [66.55, 30.977],
  [66.6631, 31.0831],
  [66.697, 31.1958],
  [66.9433, 31.3147],
  [67.0168, 31.3091],
  [67.0153, 31.2447],
  [67.0353, 31.2359],
  [67.1368, 31.2411],
  [67.2138, 31.2123],
  [67.3462, 31.2078],
  [67.5837, 31.2652],
  [67.6928, 31.3252],
  [67.7647, 31.3341],
  [67.7704, 31.3947],
  [67.6115, 31.4108],
  [67.5561, 31.5122],
  [67.569, 31.5299],
  [67.6963, 31.5208],
  [67.7812, 31.5642],
  [67.8427, 31.6235],
  [67.9661, 31.6382],
  [68.0466, 31.6884],
  [68.1386, 31.8247],
  [68.2068, 31.8079],
  [68.255, 31.7664],
  [68.3561, 31.7625],
  [68.498, 31.7252],
  [68.5498, 31.7535],
  [68.4187, 31.783],
  [68.5275, 31.823],
  [68.6758, 31.7747],
  [68.7054, 31.7013],
  [68.8038, 31.6026],
  [69.0401, 31.6731],
  [69.1143, 31.7378],
  [69.2238, 31.8819],
  [69.2989, 31.9377],
  [69.251, 32.1307],
  [69.2681, 32.3013],
  [69.228, 32.4213],
  [69.2329, 32.4627],
  [69.282, 32.5328],
  [69.3612, 32.5685],
  [69.426, 32.6551],
  [69.4147, 32.7256],
  [69.3763, 32.7719],
  [69.4773, 32.8568],
  [69.4685, 32.9943],
  [69.4877, 33.0284],
  [69.5475, 33.075],
  [69.6678, 33.077],
  [69.7718, 33.1148],
  [69.8809, 33.0892],
  [69.9558, 33.1277],
  [69.9947, 33.1273],
  [70.048, 33.1941],
  [70.1248, 33.199],
  [70.2944, 33.3189],
  [70.2857, 33.3829],
  [70.155, 33.5066],
  [70.1741, 33.6321],
  [70.1325, 33.6614],
  [70.1181, 33.7165],
  [69.9575, 33.7527],
  [69.8472, 33.9269],
  [69.8726, 34.0172],
  [70.0028, 34.0438],
  [70.3282, 33.9573],
  [70.522, 33.9387],
  [70.8622, 33.9648],
  [70.8843, 34.007],
  [70.9397, 34.0009],
  [71.0626, 34.0543],
  [71.0628, 34.1053],
  [71.1081, 34.1652],
  [71.0971, 34.2625],
  [71.122, 34.3568],
  [70.9915, 34.4431],
  [70.957, 34.532],
  [70.9858, 34.5562],
  [71.0658, 34.5585],
  [71.0806, 34.6729],
  [71.2031, 34.7482],
  [71.2895, 34.875],
  [71.4696, 34.9497],
  [71.5113, 35.0087],
  [71.5089, 35.072],
  [71.6041, 35.1382],
  [71.6379, 35.1897],
  [71.5298, 35.3009],
  [71.5316, 35.3279],
  [71.6111, 35.3955],
  [71.6224, 35.4296],
  [71.5878, 35.4709],
  [71.5842, 35.5642],
  [71.4833, 35.6266],
  [71.5198, 35.6838],
  [71.4756, 35.741],
  [71.4644, 35.7947],
  [71.4167, 35.8588],
  [71.3713, 35.8851],
  [71.3416, 35.9473],
  [71.2574, 35.9716],
  [71.1659, 36.0457],
  [71.2231, 36.1254],
  [71.3021, 36.1633],
  [71.3191, 36.2006],
  [71.3824, 36.2186],
  [71.4791, 36.3005],
  [71.5584, 36.3279],
  [71.5472, 36.3716],
  [71.6104, 36.4579],
  [71.7363, 36.3959],
  [71.7826, 36.3965],
  [71.7944, 36.4076],
  [71.7732, 36.48],
  [71.8747, 36.4991],
  [72.0388, 36.5805],
  [72.0591, 36.6271],
  [72.1716, 36.6536],
  [72.1535, 36.7021],
  [72.2136, 36.7264],
  [72.4541, 36.758],
  [72.5167, 36.8006],
  [72.6296, 36.8329],
  [72.8677, 36.8304],
  [73.1918, 36.877],
  [73.7729, 36.892],
  [74.0353, 36.8156],
  [74.0943, 36.8312],
  [74.1297, 36.8984],
  [74.2357, 36.9022],
  [74.4356, 37.0032],
  [74.5374, 36.9622],
  [74.5424, 37.0217],
  [74.6625, 37.0545],
  [74.7192, 37.0164],
  [74.7926, 37.0152],
  [74.8245, 36.9941],
  [74.8724, 36.9071],
  [74.9423, 36.977],
  [75.1185, 36.9912],
  [75.1951, 36.9489],
  [75.3135, 36.9288],
  [75.378, 36.8943],
  [75.4105, 36.7473],
  [75.4319, 36.7229],
  [75.4926, 36.7207],
  [75.508, 36.7476],
  [75.5705, 36.7667],
  [75.6498, 36.754],
  [75.7905, 36.6878],
  [75.8748, 36.6195],
  [75.9766, 36.4626],
  [75.9874, 36.4212],
  [75.9688, 36.302],
  [76.0144, 36.2304],
  [75.9701, 36.2023],
  [75.9715, 36.155],
  [75.9014, 36.1136],
  [75.9, 36.0635],
  [75.953, 36.0073],
  [76.09, 35.9781],
  [76.1381, 35.8267],
  [76.166, 35.8062],
  [76.4031, 35.831],
  [76.5372, 35.9025],
  [76.5653, 35.8655],
  [76.5511, 35.784],
  [76.5708, 35.7513],
  [76.6767, 35.7138],
  [76.7634, 35.6471],
  [76.843, 35.6413],
  [76.915, 35.5832],
  [77.039, 35.585],
  [77.1413, 35.5254],
  [77.2811, 35.528],
  [77.3837, 35.472],
  [77.5122, 35.4785],
  [77.6606, 35.4581],
  [77.7603, 35.4981],
  [77.8003, 35.4954],
];

/**
 * The country's extent, as the outline above gives it.
 *
 * DERIVED FROM THE OUTLINE rather than stated independently, which is the one
 * place this file differs from src/data/philippines.ts — that tenant has no
 * outline to derive a box from, so its bounds are a stated, rounded-outward
 * national extent. Here the two come from the same cut of the same file and
 * agree by construction: every point in the ring above is inside this box, which
 * is what `npm test` checks rather than assumes.
 *
 * It therefore carries the point of view the outline does — the eastern edge is
 * 79.62°E because the `_pak` edition's ring reaches it. Read the header. Its two
 * jobs are giving the map a home view at the right scale and giving the suite a
 * box to reject a transposed or sign-flipped coordinate against; it is not a
 * boundary, and it decides nothing about what is inside Pakistan.
 */
export const PAKISTAN_BOUNDS: GeoExtent = {
  minLat: 23.6945,
  maxLat: 37.0545,
  minLng: 60.8444,
  maxLng: 79.6207,
};
